import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

const CDN = "https://public.apptonomy.ai";

type Integration = { src?: string; alt: string; text?: string };

interface Engine {
  name: string;
  desc: string;
  integrations: Integration[];
  modules: string[];
  modulesHeader?: string;
  moduleCols?: 1 | 2;
}

// Internal 3-column template shared by the header labels and every engine card
// so the ENGINES / INTEGRATIONS / MODULES columns line up down the slide.
const COLS = "26fr 28fr 46fr";

// Per-logo render heights (px) tuned so each lockup's wordmark reads at roughly
// the same cap height (~11px) despite differing icon-to-text proportions.
const LOGO_H: Record<string, number> = {
  "app-store-lockup-on-light.svg": 16,
  "google-play-lockup-on-light.svg": 18,
  "apple-ads-on-light.svg": 17,
  "google-ads-normalized-on-light.svg": 17,
  "reddit-color.svg": 16,
  "claude-lockup-on-light.svg": 14,
  "perplexity-lockup-on-light.svg": 20,
  "chatgpt-lockup-on-light.svg": 19,
  "gemini-lockup-on-light.svg": 19,
  "gpc-lockup-on-light.svg": 13,
  "asc-lockup-on-light.svg": 14,
  "nano-banana-lockup-on-light.svg": 18,
};

const ENGINES: Engine[] = [
  {
    name: "Data Collection Engine",
    desc: "Retrieves live listing assets for your app & competitors.",
    integrations: [
      { src: "app-store-lockup-on-light.svg", alt: "App Store" },
      { src: "google-play-lockup-on-light.svg", alt: "Google Play" },
      { text: "Publisher Websites", alt: "Publisher Websites" },
    ],
    modules: ["Listing Text", "User Reviews", "App Icon", "Screenshots & Videos"],
  },
  {
    name: "ASO Engine",
    desc: "Performs in-depth research & analysis of listing assets and applies proprietary ASO logic to generate actionable recommendations.",
    integrations: [
      { src: "apple-ads-on-light.svg", alt: "Apple Ads" },
      { src: "google-ads-normalized-on-light.svg", alt: "Google Ads" },
      { src: "reddit-color.svg", alt: "Reddit" },
      { text: "Publisher Websites", alt: "Publisher Websites" },
      { src: "claude-lockup-on-light.svg", alt: "Claude" },
      { src: "perplexity-lockup-on-light.svg", alt: "Perplexity" },
      { src: "chatgpt-lockup-on-light.svg", alt: "ChatGPT" },
      { src: "gemini-lockup-on-light.svg", alt: "Gemini" },
    ],
    modulesHeader: "ASO Expert Modules",
    moduleCols: 2,
    modules: [
      "Semantic Clusters",
      "Icon & Screenshots",
      "User Sentiment",
      "Keywords",
      "AI Discoverability",
      "X-Surface Continuity",
      "User Intent",
      "Market Coverage",
      "Store Policies",
    ],
  },
  {
    name: "Revision Engine",
    desc: "Planning and shaping of a draft app listing revision. One-click publishing to go live on Apple & Google storefronts.",
    integrations: [
      { src: "gpc-lockup-on-light.svg", alt: "Google Play Console" },
      { src: "asc-lockup-on-light.svg", alt: "App Store Connect" },
      { src: "claude-lockup-on-light.svg", alt: "Claude" },
      { src: "chatgpt-lockup-on-light.svg", alt: "ChatGPT" },
      { src: "nano-banana-lockup-on-light.svg", alt: "Nano Banana" },
    ],
    modules: [
      "Text Asset Generator",
      "Image Asset Generator",
      "Text Translator",
      "Listing Publisher",
    ],
  },
  {
    name: "Analytics Engine",
    desc: "Nightly KPI ingestion and analysis of storefront conversion data.",
    integrations: [
      { src: "gpc-lockup-on-light.svg", alt: "Google Play Console" },
      { src: "asc-lockup-on-light.svg", alt: "App Store Connect" },
    ],
    modules: [
      "App Store Data Ingestor",
      "Google Play Data Ingestor",
      "Data Structuring & Cleaning",
      "Data Analysis & Warehousing",
    ],
  },
];

// Width of the left "User Experience" / step-label column, kept consistent across
// the header row, the amber step boxes, and the between-box connectors so the
// engine columns line up.
const LABEL_COL = 184;

export default function TheProduct() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-5">
      <SlideHeader eyebrow="The Platform">
        <p className="-mt-3 text-center text-lg font-semibold tracking-tight text-slate-700 md:-mt-5 md:text-xl">
          Apptonomy is the first…
        </p>
        <h1 className="-mt-1 mb-2 text-center text-5xl font-semibold tracking-tight text-violet-700 md:-mt-2 md:mb-3 md:text-7xl md:leading-[1.05]">
          ASO Operating System
        </h1>
        <p className="mx-auto text-center text-lg font-semibold tracking-tight text-slate-700 md:text-xl">
          ASO automation and workflow tooling for the AI age of app marketing.
        </p>
      </SlideHeader>

      {/* Full-width light-yellow container — fills the available height.
          Relative wrapper carries the loop-return bracket in the left gutter
          (md+): impact data from step 3 feeds the next audit in step 1. */}
      <div className="relative flex flex-col md:min-h-0 md:flex-1">
        <div
          className="pointer-events-none absolute -left-8 top-[16%] bottom-[9%] hidden w-8 md:block"
          aria-hidden="true"
        >
          {/* C-shaped return path: exits step 3, climbs the gutter, re-enters step 1 */}
          <div className="absolute inset-0 rounded-l-2xl border-y-[2.5px] border-l-[2.5px] border-blue-600"></div>
          {/* Arrowhead pointing back into step 1 */}
          <div className="absolute -top-[5px] right-0 h-0 w-0 border-y-[6px] border-l-[9px] border-y-transparent border-l-blue-600"></div>
          {/* Label chip centered on the vertical run */}
          <span className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90 whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-blue-700 ring-1 ring-blue-200">
            The loop · every day
          </span>
        </div>

      <div className="flex flex-col rounded-2xl bg-amber-50 p-3 ring-1 ring-amber-200 md:min-h-0 md:flex-1">
        {/* Header row — User Experience | Under the Hood + column labels */}
        <div className="flex gap-3">
          <div
            className="shrink-0 px-1 text-center"
            style={{ width: LABEL_COL }}
          >
            <p className="text-base font-bold leading-tight tracking-tight text-amber-600 md:text-lg">
              User Experience
            </p>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold tracking-tight text-blue-700 md:text-lg">
              Under the Hood
            </p>
            <div
              className="mt-1 grid gap-x-5 px-3"
              style={{ gridTemplateColumns: COLS }}
            >
              <ColLabel>Engines</ColLabel>
              <ColLabel>Integrations</ColLabel>
              <ColLabel>Modules</ColLabel>
            </div>
          </div>
        </div>

        {/* Step boxes (amber, full-width) with engine cards nested inside */}
        <div className="mt-2 flex flex-col gap-1.5 md:min-h-0 md:flex-1">
          <StepBox num={1} label="Run an Audit">
            <EngineCard engine={ENGINES[0]} />
            <Connector label="Cleaned Data" light />
            <EngineCard engine={ENGINES[1]} />
          </StepBox>

          <StepGap label="Actionable ASO Recommendations" />

          <StepBox num={2} label="Plan & Publish a Revision">
            <EngineCard engine={ENGINES[2]} />
          </StepBox>

          <StepGap label="Go-Live" />

          <StepBox num={3} label="Measure & Analyze Impact">
            <EngineCard engine={ENGINES[3]} />
          </StepBox>
        </div>
      </div>
      </div>
    </div>
  );
}

function StepBox({
  num,
  label,
  children,
}: {
  num: number;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3 rounded-xl bg-amber-400 p-2 shadow-sm md:min-h-0 md:flex-auto">
      <div
        className="flex shrink-0 flex-col items-center justify-center gap-2 px-1 text-center text-lg font-bold leading-tight tracking-tight text-white md:text-xl"
        style={{ width: LABEL_COL - 8 }}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-base font-bold text-amber-400 md:h-9 md:w-9 md:text-lg">
          {num}
        </span>
        <span>{label}</span>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">{children}</div>
    </div>
  );
}

// Connector label aligned to the engine column, for the gaps between step boxes.
function StepGap({ label }: { label: string }) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0" style={{ width: LABEL_COL }} />
      <div className="min-w-0 flex-1">
        <Connector label={label} />
      </div>
    </div>
  );
}

function ColLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      {children}
    </p>
  );
}

function Connector({ label, light }: { label: string; light?: boolean }) {
  return (
    <p
      className={`flex items-center gap-1.5 pl-1 text-[11px] font-medium ${
        light ? "text-white" : "text-slate-500"
      }`}
    >
      <span aria-hidden="true" className={light ? "text-white" : "text-slate-400"}>
        ↓
      </span>
      {label}
    </p>
  );
}

function EngineCard({ engine }: { engine: Engine }) {
  return (
    <div
      className="grid content-start items-start gap-x-5 rounded-lg bg-white p-3 ring-1 ring-slate-200 md:min-h-0 md:flex-auto"
      style={{ gridTemplateColumns: COLS }}
    >
      {/* Engine name + description */}
      <div>
        <p className="text-base font-bold leading-tight tracking-tight text-blue-600">
          {engine.name}
        </p>
        <p className="mt-1 text-[13px] leading-snug text-slate-600">
          {engine.desc}
        </p>
      </div>

      {/* Integrations */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {engine.integrations.map((it) =>
          it.src ? (
            <img
              key={it.alt}
              src={`${CDN}/${it.src}`}
              alt={it.alt}
              className="w-auto"
              style={{ height: `${LOGO_H[it.src] ?? 16}px` }}
              loading="lazy"
            />
          ) : (
            <span
              key={it.alt}
              className="text-sm font-semibold text-slate-600"
            >
              {it.text}
            </span>
          ),
        )}
      </div>

      {/* Modules */}
      <div>
        {engine.modulesHeader ? (
          <p className="mb-1 text-[13px] font-semibold text-slate-700">
            {engine.modulesHeader}
          </p>
        ) : null}
        <ul
          className={`grid gap-x-3 gap-y-0.5 ${
            engine.moduleCols === 2 ? "grid-cols-2" : "grid-cols-1"
          }`}
        >
          {engine.modules.map((m) => (
            <li
              key={m}
              className="flex gap-1.5 text-[13px] leading-snug text-slate-700"
            >
              <span aria-hidden="true" className="text-blue-400">
                •
              </span>
              <span>{m}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
